/**
 * The page's flow (integration plan slices 6b, 7a and 7b): town → airship → dungeon → airship → town on one canvas.
 * The page starts in a walkable scene (`?scene=`, default `la_tirana`) or, with `?run=1`, straight in a **local,
 * unverified** run (the dungeon tests' start; no server; such a run never returns). The dungeon is flown to, not
 * walked to (ADR 0032): the town's mooring boards the airship (a portal crossing inside the scene controller), and the
 * airship's wheel flies (ADR 0033): the deck casts off under the banner "Flying to {place}", then hands its
 * destination over (`SceneHandlers.onFly`).
 *
 * **The run is the server's (ADR 0002, 0034):** `POST /api/runs` starts it (the server builds the party: Brad and the
 * roster up to the party size, in the order they joined; the bag the packing panel chose, from storage, slice 8f;
 * floor 1's seed), and a run's controller
 * takes the canvas in server mode: each finished floor goes to the server before the next floor's seed is used, and
 * the run's end is the server's answer. A refused start (or no answer) moors the ship again and says why
 * (`ui.server.<code>`; an `unauthorized` says nothing here: the login shows); a run this bundle cannot play (another
 * `coreVersion` or content hash) says `ui.server.version_mismatch`, and while the server keeps such a run (`stuck`,
 * `account.ts`) the ship does not fly: it moors and the run's question shows again (update the game, or give the run
 * up). When the run ends (completed, failed or escaped, or given up after a refusal) its
 * banner shows, and `uiTiming.returnMs` after the run's end has shown (its last events played and its end banner on
 * screen: the controller's `onEnd`) the account is read again and the deck comes back moored at the wheel; the egg the
 * server granted (already among the account's eggs: a reload during the hold keeps it) brings the banner
 * `ui.banner.egg_aboard`. The hatchlings standing in the rooms leave with the traveler when a run starts.
 * `resume(run)` plays a run the server still has as `playing` (boot, after the login: `GET /api/runs/current`),
 * rebuilt from its verified floors, back to the deck's wheel when it ends.
 *
 * A swap (slice 6b fix round) never happens while the canvas's WebGL context is lost: it waits for
 * `webglcontextrestored` (the view on screen rebuilds itself first, as always). The new mode is built before the old
 * one is disposed, so a swap that throws leaves the page as it was (the old view still drawing; a return is tried
 * again `returnMs` later). Each dispose's leftovers (`renderer.info` geometries and textures) are kept (`disposals`,
 * for the tests) and warned about when not 0/0.
 *
 * The locale, the tier, the time of day (ADR 0032, 0033: a lighting preset a scene shows when it has it, chosen per
 * kind of scene: `time.ts`) and whether the stats panel shows belong to the page, not to a controller: a new
 * controller starts with them, and the pause menu changes them here.
 */
import type { ResumableRun } from '@ab/api-contract';
import type { BagEntry } from '@ab/game-core';
import type { QualityTier } from '@ab/renderer';
import type { Locale } from '@ab/session';
import { uiTiming } from '@ab/ui';
import type { AccountStore } from './account.ts';
import { type Controller, type RunRemote, createController, playable } from './controller.ts';
import { rememberBag } from './pack.ts';
import { type SceneController, type SceneStart, createSceneController, wheelSpawn } from './scene-controller.ts';
import { type TimeChoice, type TimeKind, timeKindOf } from './time.ts';

export type FlowMode =
  | { readonly kind: 'scene'; readonly id: number; readonly scene: SceneController }
  | { readonly kind: 'run'; readonly id: number; readonly run: Controller; readonly returns: boolean };

export type FlowSnapshot = { readonly mode: FlowMode; readonly stats: boolean };

export type FlowStart = { readonly kind: 'run'; readonly seed: number } | { readonly kind: 'scene'; readonly scene: string; readonly spawn: string | undefined };

export type FlowOptions = {
  readonly canvas: HTMLCanvasElement;
  readonly start: FlowStart;
  readonly tier: QualityTier;
  readonly locale: Locale;
  /** The time of day a scene shows per kind of scene (ADR 0032, 0033): a preset name, or null for each scene's default. */
  readonly time: TimeChoice;
  readonly stats: boolean;
  /** The account: the server's runs, the ship's eggs, jobs and roster (ADR 0034). */
  readonly account: AccountStore;
  /** Called after each change of mode (the boot code rebinds the canvas's gestures and the test hooks). */
  readonly onMode: (mode: FlowMode) => void;
};

export type Flow = {
  readonly snapshot: () => FlowSnapshot;
  readonly subscribe: (listener: () => void) => () => void;
  locale(): Locale;
  setLocale(locale: Locale): void;
  tier(): QualityTier;
  setTier(tier: QualityTier): void;
  /** The time of day asked for per kind of scene (ADR 0032, 0033); a run has none, and the next scene of a kind shows that kind's. */
  time(): TimeChoice;
  /** Sets the time of day of the scene shown (its kind's choice); returns that kind, or null in a run. */
  setTime(time: string): TimeKind | null;
  setStats(on: boolean): void;
  /** Sizes the current view to the canvas's settled CSS size. */
  resize(width: number, height: number): void;
  /** Plays a run the server still has as being played (from a scene), back to the deck's wheel at its end. */
  resume(run: ResumableRun): void;
  /** Runs started or resumed from a scene since boot. */
  runs(): number;
  /** What each disposed view left on the GPU, in order (0/0 each when nothing leaks). */
  disposals(): readonly { readonly geometries: number; readonly textures: number }[];
  /**
   * Each server run's return, in order, on the page's clock (`performance.now()`): `endedAt` when its end had shown
   * (the controller's `onEnd`, where the `returnMs` hold starts) and `backAt` when the deck was on screen again (null
   * until then). For the tests (slice 8j fix round: the hold measured inside the page).
   */
  returns(): readonly { readonly endedAt: number; readonly backAt: number | null }[];
};

/** Where a resumed run comes back to: the deck at its wheel. */
const deckScene = 'scene.airship_deck';

export const createFlow = (options: FlowOptions): Flow => {
  const { canvas, account } = options;
  let locale = options.locale;
  let tier = options.tier;
  let time: TimeChoice = options.time;
  let nextId = 1;
  let runs = 0;
  const disposals: { readonly geometries: number; readonly textures: number }[] = [];
  const returns: { endedAt: number; backAt: number | null }[] = [];
  const listeners = new Set<() => void>();
  const notify = (): void => {
    for (const l of listeners) l();
  };
  const sceneMode = (scene: string, spawn: string | undefined, banner?: SceneStart['banner']): FlowMode => ({
    kind: 'scene',
    id: nextId++,
    scene: createSceneController(canvas, scene, spawn, tier, locale, time, { onFly: fly }, { ship: account, ...(banner === undefined ? {} : { banner }) }),
  });
  /** Whether the canvas's WebGL context is lost now (the views made it; this only reads it). */
  const contextLost = (): boolean => (canvas.getContext('webgl2') ?? canvas.getContext('webgl'))?.isContextLost() ?? false;
  /** A swap waiting for the context to come back (one at a time). */
  let waiting = false;
  /** Runs `act` now, or once the lost context is restored (after the view on screen has rebuilt itself). */
  const whenContext = (act: () => void): void => {
    if (!contextLost()) {
      act();
      return;
    }
    if (waiting) return;
    waiting = true;
    canvas.addEventListener(
      'webglcontextrestored',
      () => {
        waiting = false;
        act();
      },
      { once: true },
    );
  };
  /** Builds the next mode, then disposes the old view and shows it; null (the old mode stays, untouched) when building throws. */
  const swap = (make: () => FlowMode, old: { dispose(): { readonly geometries: number; readonly textures: number } }): FlowMode | null => {
    let next: FlowMode;
    try {
      next = make();
    } catch (e) {
      console.warn(`the view could not be built; the page stays as it is: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
    const left = old.dispose();
    disposals.push(left);
    if (left.geometries !== 0 || left.textures !== 0) console.warn(`a disposed view left ${left.geometries} geometries and ${left.textures} textures on the GPU`);
    setMode(next);
    return next;
  };
  /**
   * Watches a server run: `returnMs` after its end has shown, the account is read again and the deck comes back at the
   * wheel, with the banner of the egg the server granted.
   */
  const watchEnd = (run: Controller, back: { readonly scene: string; readonly spawn: string }): void => {
    const record = { endedAt: 0, backAt: null as number | null };
    const leave = (): void => {
      if (snap.mode.kind !== 'run' || snap.mode.run !== run) return;
      if (run.busy() && run.snapshot().serverError === null) {
        window.setTimeout(leave, uiTiming.bannerMinHold);
        return;
      }
      whenContext(() => {
        if (snap.mode.kind !== 'run' || snap.mode.run !== run) return;
        const egg = run.serverEnd()?.egg?.species;
        const banner = egg === undefined ? undefined : { key: 'ui.banner.egg_aboard', names: { monster: egg } };
        if (swap(() => sceneMode(back.scene, back.spawn, banner), run) === null) window.setTimeout(leave, uiTiming.returnMs);
        else record.backAt = performance.now();
      });
    };
    run.onEnd(() => {
      record.endedAt = performance.now();
      returns.push(record);
      // The egg is already the account's (granted with the last floor's verification); the ship reads it again.
      void account.refresh();
      window.setTimeout(leave, uiTiming.returnMs);
    });
  };
  /** A server run takes the canvas from a scene; its end brings the traveler back to `back`. False when it could not be built. */
  const playServerRun = (run: ResumableRun, back: { readonly scene: string; readonly spawn: string }, from: SceneController): boolean => {
    const remote: RunRemote = { submit: (floor, body) => account.api.submitFloor(run.runId, floor, body) };
    const next = swap(() => ({ kind: 'run', id: nextId++, run: createController(canvas, { kind: 'server', run, remote }, tier, locale), returns: true }), from);
    if (next?.kind !== 'run') return false;
    runs += 1;
    // The traveler has left the ship: the hatchlings standing aboard go with him (the roster keeps them).
    account.clearGuests();
    watchEnd(next.run, back);
    return true;
  };
  /**
   * The airship flies to `destination` (ADR 0033) with the bag the packing panel chose (slice 8f, ADR 0041): the server
   * starts a run of it, which takes the canvas; its end brings the traveler back to `spawn` of `from`. The bag is
   * remembered for the account's next panel once the server took it; refused (`bad_bag`: storage changed meanwhile),
   * the ship moors and the panel opens again with "Check the bag." over storage as read again.
   */
  function fly(destination: string, spawn: string, from: string, bag: readonly BagEntry[]): void {
    if (snap.mode.kind !== 'scene') return;
    const scene = snap.mode.scene;
    // The ship moors again and says why when the run cannot start.
    const refused = (key: string): void => {
      scene.land();
      scene.say(null, [key]);
    };
    // A run the server keeps that this build cannot play: not abandoned by a new one without the player's say.
    if (account.run().kind === 'stuck') {
      scene.land();
      account.askAboutRun();
      return;
    }
    void account.api.startRun(destination, bag).then((r) => {
      if (snap.mode.kind !== 'scene' || snap.mode.scene !== scene) return;
      if (!r.ok) {
        // Unauthorized: the account signed out and the login is up; it says what there is to say.
        if (r.error === 'unauthorized') scene.land();
        else if (r.error === 'bad_bag') {
          // Whatever the reason, the panel again over storage as the server has it now.
          scene.land();
          scene.repack(destination, bag);
        } else refused(`ui.server.${r.error}`);
        return;
      }
      const who = account.id();
      if (who !== null) rememberBag(who, r.value.run.bag);
      if (!playable(r.value.run)) {
        refused('ui.server.version_mismatch');
        return;
      }
      whenContext(() => {
        if (snap.mode.kind !== 'scene' || snap.mode.scene !== scene) return;
        // The run could not be built: the ship moors again.
        if (!playServerRun({ ...r.value.run, floors: [] }, { scene: from, spawn }, scene)) scene.land();
      });
    });
  }
  const setMode = (mode: FlowMode): void => {
    snap = { ...snap, mode };
    options.onMode(mode);
    notify();
  };
  const start = options.start;
  let snap: FlowSnapshot = {
    mode: start.kind === 'run' ? { kind: 'run', id: nextId++, run: createController(canvas, { kind: 'local', seed: start.seed }, tier, locale), returns: false } : sceneMode(start.scene, start.spawn),
    stats: options.stats,
  };
  options.onMode(snap.mode);
  const current = (): { setLocale(l: Locale): void; setTier(t: QualityTier): void; resize(w: number, h: number): void } => (snap.mode.kind === 'scene' ? snap.mode.scene : snap.mode.run);
  return {
    snapshot: () => snap,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    locale: () => locale,
    setLocale(next) {
      locale = next;
      current().setLocale(next);
    },
    tier: () => tier,
    setTier(next) {
      tier = next;
      current().setTier(next);
    },
    time: () => time,
    setTime(next) {
      if (snap.mode.kind !== 'scene') return null;
      const kind = timeKindOf(snap.mode.scene.state().scene.kind);
      time = { ...time, [kind]: next };
      snap.mode.scene.setLighting(next);
      return kind;
    },
    setStats(on) {
      snap = { ...snap, stats: on };
      notify();
    },
    resize(width, height) {
      current().resize(width, height);
    },
    resume(run) {
      if (snap.mode.kind !== 'scene' || !playable(run)) return;
      const scene = snap.mode.scene;
      whenContext(() => {
        if (snap.mode.kind !== 'scene' || snap.mode.scene !== scene) return;
        playServerRun(run, { scene: deckScene, spawn: wheelSpawn(deckScene) ?? 'gangplank' }, scene);
      });
    },
    runs: () => runs,
    disposals: () => [...disposals],
    returns: () => returns.map((r) => ({ ...r })),
  };
};
